import { Actions, type SetPopupStateActionData, openPopup, setPopupState } from "@client/actions";
import { isBrowser, setCustomTimeout, waitUntil } from "@client/lib/misc";
import { type PopupTrigger, getPopupTrigger, shouldShowPopup } from "@client/lib/popup";
import { activePopupSelector } from "@client/selectors";
import { type AppState } from "@client/types";
import { type ReduxAction } from "@client/types/ReduxAction";
import { type MiddlewareAPI } from "redux";
import { type ThunkDispatch } from "redux-thunk";

class ExitIntent {
  private _isTracking: boolean = false;

  public track = (callback: () => void) => {
    this._isTracking = true;

    window.addEventListener("mouseout", function (event) {
      // Check if the mouse is truly leaving the window, not just entering a child element
      // The 'relatedTarget' property is null when the mouse leaves the document
      if (event.relatedTarget === null) {
        callback();
      }
    });
  };

  public get isTracking() {
    return this._isTracking;
  }
}

type ScheduledPopupDelay = {
  id: string;
  timeout: NodeJS.Timeout;
  trigger: PopupTrigger;
};

type Show = (id: string, onShow?: () => void) => void;

class PopupDelay {
  private _showingId: string | null;
  private _scheduled: ScheduledPopupDelay[] = [];

  public add = (id: string, trigger: PopupTrigger, show: Show) => {
    this._scheduled.push({
      id,
      timeout: this._schedule(id, trigger.delay!, show),
      trigger
    });
  };

  public clear = () => {
    this._scheduled.forEach((x) => clearTimeout(x.timeout));
    this._scheduled = [];
  };

  public handleCloseActive = (show: Show) => {
    if (!this._showingId) return;
    this._reschedule(this._showingId, show);
    this._showingId = null;
  };

  private _reschedule = (id: string, show: Show) => {
    var schedule = this._scheduled.find((x) => x.id === id);
    if (!schedule) return;

    schedule.timeout = this._schedule(id, schedule.trigger.frequency!, show);
  };

  private _schedule = (id: string, delay: string, show: Show) => {
    return setCustomTimeout(() => {
      show(id, () => (this._showingId = id));
    }, delay!);
  };
}

const exitIntent = new ExitIntent();
const popupDelay = new PopupDelay();
export default (api: MiddlewareAPI<ThunkDispatch<any, any, any>, AppState>) => (next) => (action: ReduxAction<any>) => {
  next(action);

  const showPopup = (id: string, onShow?: () => void) => {
    const activePopup = activePopupSelector(api.getState());
    if (activePopup?.id !== id) {
      // TODO: better solution would untrack ExitIntent when popup is shown
      waitUntil(
        () => !activePopupSelector(api.getState()),
        () => (api.dispatch(openPopup(id)), onShow && onShow()),
        1000
      );
    }
  };

  switch (action.type) {
    case Actions.LoadPageBegin: {
      popupDelay.clear();

      const activePopup = activePopupSelector(api.getState());
      if (activePopup) {
        api.dispatch(
          setPopupState({
            id: activePopup.id,
            state: "closed"
          })
        );
      }
      break;
    }

    case Actions.SetPopupState: {
      const data = action.data as SetPopupStateActionData;
      if (data.state === "closed") {
        popupDelay.handleCloseActive(showPopup);
      }
      break;
    }

    case Actions.SsrInit:
    case Actions.InitSuccess:
    case Actions.LoadPageSuccess: {
      if (isBrowser()) {
        const state = api.getState();

        for (const pagePopup of state.pagePopups) {
          // TODO_NEXT3: optimize
          if (!shouldShowPopup(state, pagePopup.id)) continue;

          const trigger = getPopupTrigger(state, pagePopup.id);
          if (!trigger) continue;

          switch (trigger.event) {
            case "exitIntent": {
              // We cannot have more than one popup with exit intent
              if (!exitIntent.isTracking) {
                exitIntent.track(() => showPopup(pagePopup.id));
              }
              break;
            }
            case "pageLoad": {
              if (trigger.delay) {
                popupDelay.add(pagePopup.id, trigger, showPopup);
              }
            }
          }
        }
      }

      break;
    }
  }
};
